import {
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
} from 'react';
import './Modal.css';

const FOCUSABLE =
  'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

interface ModalProps {
  title: string;
  onClose: () => void;
  className?: string;
  children: ReactNode;
}

/**
 * Оболочка новых модальных окон: фокус внутри, Escape и щелчок по фону
 * закрывают. Закрытие ведёт к безопасному исходу — отменяет операцию, а не
 * подтверждает её.
 */
const Modal = ({ title, onClose, className = '', children }: ModalProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Фокус возвращается туда, откуда окно открыли.
  useEffect(() => {
    const openedFrom = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();

    return () => openedFrom?.focus?.();
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }

      if (event.key !== 'Tab') return;

      // Без этого Tab уводит фокус на кнопки под окном.
      const focusable = Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [],
      );

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (
        event.shiftKey &&
        (active === first || active === dialogRef.current)
      ) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  // mouseDown, а не click: выделение текста, отпущенное над фоном, не
  // должно закрывать окно — адрес здесь выделяют, чтобы сверить.
  const handleOverlayMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) onClose();
  };

  return (
    <div
      className="modal_overlay"
      role="presentation"
      onMouseDown={handleOverlayMouseDown}
    >
      <div
        ref={dialogRef}
        className={`modal ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        <h4 id={titleId}>{title}</h4>
        {children}
      </div>
    </div>
  );
};

export default Modal;
